<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="working">
					<app-heading title="Working Jobs">
						<template slot="right">
						</template>
					</app-heading>
					<app-row>
						<app-cell>
							<app-data-table
								:items="jobs"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								@update="fetchJobs"
							>
								<template v-slot:data="{ item }">
									<app-json :data="item.data" />
								</template>
								<template v-slot:progress="{ item }">
									<div v-if="getProgress(item).progress">
										<span class="text-caption">
											{{getProgress(item).progress | fixed(0) }}%
										</span>&nbsp;&nbsp;
										<span
											v-if="getProgress(item).description"
											class="text-caption"
										>({{getProgress(item).description}})</span>
										<app-progress-bar :value="getProgress(item).progress" />
									</div>
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										v-if="$ravello.can('system.post')"
										color="secondary"
										icon="fa-terminal"
										table
										@click="onTrail(item.id)"
									>
										Trail
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('system.post')"
										color="warning"
										icon="fa-ban"
										table
										label-confirm="Fail"
										:description="`Are you sure you want to force the failure of the job`
											+`<b>${item.id}</b>?`"
										title="Fail Job"
										@click="onFail(item.id)"
									>
										Fail
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>
				<app-dialog v-model="trails.show" max-width="600" title="Trail">
					<app-dialog-body class="jobs-working-trail pt-2">
						<div class="console">
							<app-row v-for="(trail, i) in trails.data" :key="`trail-${i}`">
								<app-cell class="pl-1 pr-1 pb-0 pt-1" cols="12">
									<span v-if="/ERRORED/.test(trail)" class="error--text">{{trail}}</span>
									<span v-else>{{trail}}</span>
								</app-cell>
							</app-row>
						</div>
					</app-dialog-body>
					<app-dialog-footer>
						<app-button
							color="primary"
							flat="flat"
							@click="trails.show = false"
						>
							Close
						</app-button>
					</app-dialog-footer>
				</app-dialog>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { jobsTabs } from '@/datasources/tabs'
import { mapAll } from '@/utils/async'

export default {
	data() {
		return {
			jobs: [],
			tabs: jobsTabs,
			count: -1,
			refresh: false,
			trails: {
				show: false,
				id: null,
				data: [],
			},
			headers: [{
				text: 'ID',
				value: 'id',
				sortable: false,
			}, {
				text: 'Job',
				value: 'name',
				sortable: false,
			}, {
				text: 'Data',
				value: 'data',
				sortable: false,
			}, {
				text: 'Progression',
				value: 'progress',
				width: '20%',
				sortable: false,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				width: '20%',
				sortable: false,
			}],
			progress: {},
			socket: null,
			updateInterval: null,
			progressInterval: null,
			trailInterval: null,
		}
	},
	mounted() {
		this.socket = this.$ravello.socket('jobs')
		this.socket.on('started', (id) => {
			this.fetchJob(id)
		})
		this.socket.on('progress', ({ id, progress, description }) => {
			this.updateProgress(id, progress, description)
		})
		this.socket.on('completed', (id) => {
			if (this.trails.id) {
				this.trails.data.unshift('------')
			}
			this.clearJob(id)
		})
		this.socket.on('failed', (id) => {
			this.clearJob(id)
		})
	},
	watch: {
		'trail.show': {
			handler(value) {
				if (value) { return	}
				this.socket.off(`trail:${this.trails.id}`)
				this.trails.id = null
			},
		},
	},
	methods: {
		async fetchProgress() {
			const ids = this.jobs.map(job => job.id)
			const progression = await mapAll(this.jobs, async (job) => {
				try {
					const progress = await this.$ravello.request()
						.to('system')
						.to('jobs')
						.to('working')
						.to(job.id)
						.to('progress')
						.get()

					// The job is be completed so adjust to 100
					if (!Object.keys(progress || {}).length) {
						return {
							progress: 100,
						}
					}
					return progress
				// eslint-disable-next-line no-empty
				} catch (err) {
					return {}
				}
			})

			this.progress = ids.reduce((acc, id, i) => ({
				...acc,
				[id]: progression[i],
			}), {})
		},

		async fetchJobs(pagination) {
			const request = this.$ravello.request()
				.to('system')
				.to('jobs')
				.to('working')
				.fromPagination(pagination)

			try {
				this.jobs = await request.get()
				if (this.count === -1) {
					const count = await request.count()
					this.count = count.count
				}
				await this.fetchProgress()
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async fetchJob(id) {
			if (this.jobs.some(job => job.id === id)) { return }
			const request = this.$ravello.request()
				.to('system')
				.to('jobs')
				.to('working')
				.to(id)

			try {
				const job = await request.get()
				this.progress = {
					...this.progress,
					[id]: { progress: 0 },
				}
				this.jobs.unshift(job)
				this.count += 1
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		updateProgress(id, progress, description) {
			const { progress: jobProgress = 0, description: jobDescription } = this.progress[id] || {}

			this.progress[id] = {
				progress: parseFloat(jobProgress) + parseFloat(progress),
				description: jobDescription && !description
					? jobDescription : description,
			}
		},

		clearJob(id) {
			const index = this.jobs.findIndex(job => job.id === id)
			if (index > -1) {
				this.jobs.splice(index, 1)
				this.count -= 1
			}
			delete this.progress[id]
		},

		async fetchTrail(id) {
			const request = this.$ravello.request()
				.to('system')
				.to('jobs')
				.to('working')
				.to(id)
				.to('trail')

			try {
				const trails = await request.get()
				if (this.trails.data.length && !trails.length) {
					this.trails.data.unshift('------')
					return
				}
				this.trails.data = trails
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
		getProgress({ id }) {
			return this.progress[id] || {
				progress: 0,
			}
		},

		async onTrail(id) {
			this.trails.show = true
			this.trails.id = id
			this.trails.data = []

			await this.fetchTrail(id)
			this.socket.on(`trail:${id}`, (message) => {
				this.trails.data.unshift(message)
			})
		},
		async onFail(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.to('system')
					.to('jobs')
					.to('working')
					.to(id)
					.to('fail')
					.create()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
	},
	beforeDestroy() {
		if (this.socket) {
			this.socket.disconnect()
		}
	},
}
</script>

<style>
	/* @todo - Maybe have a component for that */
	.jobs-working-trail .console {
		background: black;
		color: gray;
		height: 600px;
		max-height: 600px;
		overflow-x: hidden;
    overflow-y: auto;
    word-break: break-word;
	}
</style>
